<script>
import { ref, watch } from 'vue'
import ErrorBlock from '@/views/programs/checkup/components/ErrorBlock.vue'

export default {
  components: { ErrorBlock },
  props: {
    isModalOpen: Boolean,
    formError: Object || null,
    loading: { type: Boolean }
  },
  emits: ['sendScheduleRequest', 'closeModal'],
  setup (props, { emit }) {
    const errors = ref({})

    const userForm = ref({
      name: '',
      email: '',
      phone: '',
      agreed: false,
      read: false
    })

    const schedule = () => {
      if (!userForm.value.name) errors.value.name = 'Введите ФИО'
      if (!userForm.value.email) errors.value.email = 'Введите E-mail'
      if (!userForm.value.phone) errors.value.phone = 'Введите номер телефона'
      if (!userForm.value.agreed) {
        errors.value.agreed =
          'Необходимо принять согласие на обработку персональных данных'
      }
      if (!userForm.value.read) {
        errors.value.read =
          'Необходимо подтвердить, что вы ознакомлены с правилами внутреннего распорядка'
      }

      if (Object.keys(errors.value).length === 0) {
        emit('sendScheduleRequest', userForm.value)
        userForm.value = {
          name: '',
          email: '',
          phone: '',
          agreed: false,
          read: false
        }
      }
    }

    watch(
      userForm,
      () => {
        errors.value = {}
      },
      { deep: true }
    )

    function overlayClickHandler (event) {
      if (event.target === event.currentTarget) {
        emit('closeModal')
      }
    }

    return {
      userForm,
      errors,
      schedule,
      overlayClickHandler
    }
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="overlay"> </Transition>
  </Teleport>
  <div
    class="modal-auth__overlay"
    :class="{ open: isModalOpen }"
    @click="overlayClickHandler"
  >
    <div class="modal-auth">
      <div class="modal-auth__top">
        <div class="modal-auth__title">
          Заполните личные данные для записи на чекап
        </div>
        <div class="modal-auth__close" @click.prevent="$emit('closeModal')">
          <svg>
            <use xlink:href="/uploads/img/sprite.svg#icon-reset"></use>
          </svg>
        </div>
      </div>

      <form class="modal-auth__form">
        <div class="modal-auth__form-item">
          <input
            class="modal-auth__form-input"
            type="text"
            placeholder="ФИО"
            v-model="userForm.name"
          />
          <div class="modal-auth__error" :class="errors?.name ? 'show' : ''">
            {{ errors?.name }}
          </div>
        </div>

        <div class="modal-auth__form-item">
          <input
            class="modal-auth__form-input"
            type="text"
            placeholder="E-mail"
            v-model="userForm.email"
          />
          <div class="modal-auth__error" :class="errors?.email ? 'show' : ''">
            {{ errors?.email }}
          </div>
        </div>

        <div class="modal-auth__form-item">
          <input
            class="modal-auth__form-input"
            type="text"
            placeholder="Номер телефона"
            v-model="userForm.phone"
          />
          <div class="modal-auth__error" :class="errors?.phone ? 'show' : ''">
            {{ errors?.phone }}
          </div>
        </div>

        <div class="modal-auth__form-item">
          <label class="modal-auth__confirm">
            <input
              class="modal-auth__checkbox"
              type="checkbox"
              v-model="userForm.agreed"
            />
            Согласие на
            <a class="modal-auth__link" href="https://www.k31.ru/info/soglasie/" target="_blank"
              >обработку персональных данных</a
            >
          </label>

          <div class="modal-auth__error" :class="errors?.agreed ? 'show' : ''">
            {{ errors?.agreed }}
          </div>
        </div>

        <div class="modal-auth__form-item">
          <label class="modal-auth__confirm">
            <input
              class="modal-auth__checkbox"
              type="checkbox"
              v-model="userForm.read"
            />
            Я ознакомлен с
            <a href="https://www.k31.ru/uploads/pdf/prikaz_n18-6.pdf" target="_blank"
              >правилами внутреннего распорядка</a
            >
          </label>
          <div class="modal-auth__error" :class="errors?.read ? 'show' : ''">
            {{ errors?.read }}
          </div>
        </div>

        <button
          @click.prevent="schedule"
          :disabled="loading"
          class="modal-auth__submit"
        >
          Подтвердить
          <div class="loader" v-if="loading"></div>
        </button>

        <ErrorBlock v-if="formError?.message">{{
          formError?.message
        }}</ErrorBlock>
      </form>
    </div>
  </div>
</template>

<style scoped lang="scss">
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}
.modal-auth {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10001;
  min-width: 320px;
  background-color: #fff;
  border-radius: 4px;
  padding: 30px;
  max-height: 90vh;
  border: 1px solid $blue-grey;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    top: 45%;
    max-height: 85vh;
  }

  &__overlay {
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 10000;
    background-color: rgba(0, 40, 86, 0.8);
    display: none;
    &.open {
      display: block;
    }
  }

  &__close {
    border: 1px solid #bac7de;
    background-color: transparent;
    cursor: pointer;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    flex-shrink: 0;
    svg {
      width: 8px;
      height: 8px;
    }
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;
    gap: 20px;

    @media (max-width: 768px) {
      flex-direction: row !important;
      align-items: flex-start;
    }
  }

  &__title {
    font-weight: 600;
    font-size: 20px;
    line-height: 100%;
    letter-spacing: 0%;
  }

  &__form {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;
    margin: 0;
  }

  &__form-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 14px;
    font-weight: 350;
    line-height: 14px;
    text-align: left;
    color: #002856;
  }

  &__form-input {
    color: #002856;
    border: 1px solid #bac7de;
    border-radius: 9px;
    padding-left: 13px;
    height: 40px;
    font-size: 16px;
  }

  &__confirm {
    font-size: 16px;
    input {
      cursor: pointer;
    }
    a {
      text-decoration: underline;
      color: #00499c;
      &:hover {
        color: #0077ff;
      }
    }
  }

  &__checkbox {
    height: auto;
    margin-right: 5px;
  }

  &__error {
    font-size: 12px;
    color: red;
    min-height: 20px;
  }

  &__submit {
    color: #002856;
    font-size: 16px;
    font-weight: 400;
    line-height: 23.58px;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #e1e8f4;
    border-radius: 30px;
    height: 50px;
    width: 100%;
    text-transform: uppercase;
    transition: 0.2s;
    border: none;
    &:disabled {
      cursor: auto;
    }
  }
}

.loader {
  width: 30px;
  padding: 8px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #002856;
  --_m: conic-gradient(#0000 10%, #000), linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m);
  mask: var(--_m);
  -webkit-mask-composite: source-out;
  mask-composite: subtract;
  animation: l3 1s infinite linear;
}
@keyframes l3 {
  to {
    transform: rotate(1turn);
  }
}
</style>
